import React, { useEffect, useState } from 'react';
import { Box, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { directShipApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';

const slug = (s) => s.replace(/\s+/g, '-');

const SiteGroup = ({ g, picked, onToggle, onAll }) => {
  const all = g.boxes.every((b) => picked.has(b.box_number));
  return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid={`ds-site-${slug(g.site)}`}>
      <label className="flex cursor-pointer items-center gap-2 border-b bg-slate-50 px-3 py-2 text-sm font-semibold">
        <Checkbox checked={all} onCheckedChange={() => onAll(g, !all)} data-testid={`ds-site-all-${slug(g.site)}`} />
        <Truck className="w-4 h-4 text-[#2D5F4F]" />{g.site}<span className="font-normal text-gray-500">· {g.boxes.length} box(es)</span>
      </label>
      <div className="max-h-56 divide-y overflow-y-auto">
        {g.boxes.map((b) => (
          <label key={b.box_number} className="flex cursor-pointer items-center gap-3 px-3 py-1.5 text-sm hover:bg-amber-50" data-testid={`ds-box-${b.box_number}`}>
            <Checkbox checked={picked.has(b.box_number)} onCheckedChange={() => onToggle(b.box_number, g.site)} data-testid={`ds-box-pick-${b.box_number}`} />
            <Box className="w-4 h-4 text-gray-400" /><span className="font-mono font-semibold">{b.box_number}</span>
            <span className="text-gray-600">{b.total_bundles} bundle(s) · {b.total_usns} pcs</span>
            <span className="ml-auto text-xs text-gray-500">{fmtDT(b.created_at)} · {b.created_by_name}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

/** Tick the Direct Ship boxes that went with one Transfer Out (all to one site) — the pieces are checked item-wise on save. */
export const ShipBoxesDialog = ({ to, onClose, onSaved }) => {
  const [sites, setSites] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!to) return;
    setSites(null);
    directShipApi.boxes(to.id).then((s) => {
      setSites(s);
      const mine = s.find((g) => g.boxes.some((b) => b.ticked));
      setPicked(new Set(mine ? mine.boxes.filter((b) => b.ticked).map((b) => b.box_number) : []));
      setSite(mine ? mine.site : null);
    }).catch((e) => { setSites([]); toast.error(errMsg(e, 'Could not load the boxes')); });
  }, [to]);
  const set = (nos, on, s) => setPicked((p) => {
    const n = new Set(site === s ? p : []);
    nos.forEach((x) => (on ? n.add(x) : n.delete(x)));
    setSite(n.size ? s : null);
    return n;
  });
  const pcs = (sites || []).flatMap((g) => g.boxes).filter((b) => picked.has(b.box_number)).reduce((n, b) => n + (b.total_usns || 0), 0);
  const save = () => {
    setBusy(true);
    directShipApi.setBoxes(to.id, [...picked]).then((r) => {
      if (r.state === 'ok') toast.success(`Pieces match — ${r.doc_no} is ready for its e-way bill`);
      else toast.warning(r.state === 'mismatch' ? `Pieces don't match — Transfer Out ${r.qty} pcs, boxes ${r.box_pcs} pcs` : 'Boxes saved', { duration: 8000 });
      onSaved(r);
    }).catch((e) => toast.error(errMsg(e, 'Could not save the boxes'), { duration: 8000 })).finally(() => setBusy(false));
  };
  return (
    <Dialog open={!!to} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ds-boxes-dialog">
        <DialogHeader><DialogTitle>Boxes on Transfer Out {to?.doc_no}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Tick the boxes that went with this Transfer Out (<b>{to?.party}</b>) — all to <b>one site</b>. The pieces per item must match the Transfer Out before the e-way bill can be generated.</p>
        <div className="space-y-3">
          {sites === null ? <Loader2 className="w-6 h-6 mx-auto my-8 animate-spin text-amber-600" />
            : sites.length === 0 ? <p className="rounded-lg bg-slate-50 py-10 text-center text-sm text-gray-500" data-testid="ds-boxes-empty">No Direct Ship boxes to our sites in the last 30 days that aren't on another Transfer Out</p>
              : sites.map((g) => <SiteGroup key={g.site} g={g} picked={site === g.site ? picked : new Set()} onToggle={(no, s) => set([no], !(site === s && picked.has(no)), s)}
                onAll={(x, on) => set(x.boxes.map((b) => b.box_number), on, x.site)} />)}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <span className={`text-sm font-semibold ${picked.size && pcs === Number(to?.qty) ? 'text-emerald-700' : 'text-gray-700'}`} data-testid="ds-boxes-pcs">
            Ticked {picked.size} box(es) · {pcs} pcs · Transfer Out {to?.qty} pcs</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="ds-boxes-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy || sites === null} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ds-boxes-save-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save boxes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShipBoxesDialog;
